import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { 
  Button, 
  TextField, 
  Box, 
  Typography,
  Paper,
  InputLabel,
  FormHelperText
} from "@mui/material";
import { toast } from "react-toastify";
import * as Yup from "yup";
import useApiCaller from "hooks/apiCaller";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const CreateDashboardForm = ({ onDashboardCreated }) => {
  const apiCaller = useApiCaller();

  const validationSchema = Yup.object().shape({
    dashboardName: Yup.string().required("Dashboard Name is required"),
    dashboardFile: Yup.mixed()
      .required("Dashboard File is required")
      .test("fileType", "Only CSV and Excel files are allowed", (value) => {
        if (!value) return true;
        const fileType = value.name.split('.').pop().toLowerCase();
        return ['csv', 'xlsx', 'xls'].includes(fileType);
      }),
  });

  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append("dashboardName", values.dashboardName);
    formData.append("companyId", "100");
    formData.append("dashboardFile", values.dashboardFile);

    try {
      const response = await apiCaller.post("/super-user/create-dashboard", formData, {
        headers: { 
          "Content-Type": "multipart/form-data",
        },
      });

      if (response) {
        toast.success("Dashboard Created Successfully!", { theme: "colored" });
        if (onDashboardCreated) {
          onDashboardCreated();
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to create dashboard.");
    }
  };

  return (
    <Paper 
      elevation={2}
      sx={{
        p: 3,
        borderRadius: '8px',
        maxWidth: '600px',
        margin: '0 auto',
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 3,
          color: '#1976d2',
          fontWeight: 500,
        }}
      >
        Upload New Dashboard
      </Typography>
      
      <Formik
        initialValues={{
          dashboardName: "",
          companyId: "",
          dashboardFile: null,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values }) => (
          <Form>
            {/* Dashboard Name */}
            <Box sx={{ mb: 3 }}>
              <InputLabel sx={{ mb: 1, color: '#333' }}>Dashboard Name</InputLabel>
              <Field
                as={TextField}
                name="dashboardName"
                placeholder="Enter dashboard name"
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />
              <ErrorMessage name="dashboardName">
                {(msg) => <FormHelperText error>{msg}</FormHelperText>}
              </ErrorMessage>
            </Box>

        
            {/* Dashboard File Upload */}
            <Box sx={{ mb: 3 }}>
              <InputLabel sx={{ mb: 1, color: '#333' }}>Dashboard File</InputLabel>
              <Box
                sx={{
                  border: '2px dashed #1976d2',
                  borderRadius: '8px',
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                }}
              >
                <input
                  type="file"
                  name="dashboardFile"
                  accept=".csv,.xlsx,.xls"
                  onChange={(event) =>
                    setFieldValue("dashboardFile", event.currentTarget.files[0])
                  }
                  style={{ display: 'none' }}
                  id="dashboard-file-upload"
                />
                <label htmlFor="dashboard-file-upload">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CloudUploadIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                    <Typography variant="body1" sx={{ color: '#1976d2' }}>
                      {values.dashboardFile ? values.dashboardFile.name : 'Click to upload or drag and drop'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', mt: 1 }}>
                      Supported formats: CSV, XLSX, XLS
                    </Typography>
                  </Box>
                </label>
              </Box>
              <ErrorMessage name="dashboardFile">
                {(msg) => <FormHelperText error>{msg}</FormHelperText>}
              </ErrorMessage>
            </Box>

            {/* Submit Button */}
            <Button
              type="submit" 
              variant="contained" 
              fullWidth 
              sx={{ 
                mt: 2,
                py: 1.5,
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              Upload Dashboard
            </Button>
          </Form>
        )}
      </Formik>
    </Paper>
  );
};

export default CreateDashboardForm; 